import React, { useState, useEffect, useRef } from "react";
import { Icon } from '../components/icons';
import "./Championships.css";

/* ============ ICONE LOCALI (non presenti in icons.jsx) ============ */
const Svg = ({ children, size = 16, className = '', ...rest }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
        focusable="false"
        {...rest}
    >
        {children}
    </svg>
);

const IconTrash = (props) => (
    <Svg {...props}>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </Svg>
);

const IconUpload = (props) => (
    <Svg {...props}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </Svg>
);

const IconImage = (props) => (
    <Svg {...props}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
    </Svg>
);

const IconCalendar = (props) => (
    <Svg {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </Svg>
);

/* ============ Dropzone ============ */
function Dropzone({ file, preview, onFileChange }) {
    const inputRef = useRef(null);
    const [isOver, setIsOver] = useState(false);

    const onDrop = (e) => {
        e.preventDefault();
        setIsOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFileChange(f);
    };

    const onClick = () => inputRef.current?.click();

    const clearFile = (e) => {
        e.stopPropagation();
        onFileChange(null);
    };

    return (
        <div
            className={`rs-dropzone champ-dropzone ${isOver ? 'is-over' : ''} ${preview ? 'champ-dropzone--file' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
            onDragLeave={() => setIsOver(false)}
            onDrop={onDrop}
            onClick={onClick}
            role="button"
            tabIndex={0}
        >
            {preview ? (
                <div className="champ-dropzone__preview">
                    <img src={preview} alt="anteprima" />
                    <div className="champ-dropzone__meta">
                        <span className="champ-dropzone__name">{file?.name || 'Immagine'}</span>
                        <span className="champ-dropzone__hint">Clicca per sostituire</span>
                    </div>
                    <button className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon" onClick={clearFile} type="button" title="Rimuovi immagine" aria-label="Rimuovi immagine">
                        <Icon.X size={14} />
                    </button>
                </div>
            ) : (
                <>
                    <IconUpload size={22} />
                    <strong>Immagine campionato</strong>
                    <small>Trascina o clicca · consigliato 16:9</small>
                </>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onFileChange(f);
                }}
            />
        </div>
    );
}

/* ============ Main Component ============ */
export default function Championships() {
    const [championships, setChampionships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [error, setError] = useState("");
    const [creating, setCreating] = useState(false);
    const [busyById, setBusyById] = useState({});
    const [formulaInputs, setFormulaInputs] = useState({});

    // Solo presentazione: pannello di creazione collassabile e conferma eliminazione inline
    const [formOpen, setFormOpen] = useState(false);
    const [confirmId, setConfirmId] = useState(null);

    const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

    // Load championships
    useEffect(() => {
        let alive = true;
        setLoading(true);
        fetch(`${API_BASE}/api/championships`)
            .then((res) => res.json())
            .then((data) => {
                if (!alive) return;
                setChampionships(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!alive) return;
                setChampionships([]);
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => { alive = false; };
    }, [API_BASE]);

    // File handling
    const handleFile = (file) => {
        if (!file) {
            setPhotoFile(null);
            setPhotoPreview(null);
            return;
        }
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(file);
    };

    // Create championship
    const handleAddChampionship = async (e) => {
        e.preventDefault();
        setError("");
        if (!name.trim()) {
            setError("Inserisci il nome del campionato");
            return;
        }
        try {
            setCreating(true);
            const formData = new FormData();
            formData.append("name", name.trim());
            if (photoFile) formData.append("photo", photoFile);

            const res = await fetch(`${API_BASE}/api/championships`, {
                method: "POST",
                body: formData,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setChampionships(Array.isArray(data) ? data : []);
            setName("");
            setPhotoFile(null);
            setPhotoPreview(null);
        } catch (err) {
            console.error(err);
            setError("Errore durante il salvataggio del campionato.");
        } finally {
            setCreating(false);
        }
    };

    // Delete championship (la conferma avviene nel riquadro .rs-confirm inline; prima: window.confirm)
    const handleDeleteChampionship = async (id) => {
        setBusyById((s) => ({ ...s, [id]: true }));
        try {
            const res = await fetch(`${API_BASE}/api/championships/${id}`, {
                method: "DELETE",
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setChampionships(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            alert("Errore durante l'eliminazione del campionato.");
        } finally {
            setBusyById((s) => ({ ...s, [id]: false }));
        }
    };

    // Add formula
    const handleAddFormula = async (champId) => {
        const label = (formulaInputs[champId] || "").trim();
        if (!label) return;
        setBusyById((s) => ({ ...s, [champId]: true }));
        try {
            const res = await fetch(`${API_BASE}/api/championships/${champId}/formulas`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ label }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updatedChamp = await res.json();
            setChampionships((prev) =>
                prev.map((c) => (c.id === updatedChamp.id ? updatedChamp : c))
            );
            setFormulaInputs((s) => ({ ...s, [champId]: "" }));
        } catch (err) {
            console.error(err);
            alert("Errore durante l'aggiunta della formula.");
        } finally {
            setBusyById((s) => ({ ...s, [champId]: false }));
        }
    };

    // Delete formula
    const handleDeleteFormula = async (champId, formulaId) => {
        setBusyById((s) => ({ ...s, [champId]: true }));
        try {
            const res = await fetch(`${API_BASE}/api/championships/${champId}/formulas/${formulaId}`, {
                method: "DELETE",
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updatedChamp = await res.json();
            setChampionships((prev) =>
                prev.map((c) => (c.id === updatedChamp.id ? updatedChamp : c))
            );
        } catch (err) {
            console.error(err);
            alert("Errore durante l'eliminazione della formula.");
        } finally {
            setBusyById((s) => ({ ...s, [champId]: false }));
        }
    };

    const onChangeFormulaInput = (champId, value) => {
        setFormulaInputs((s) => ({ ...s, [champId]: value }));
    };

    // Stats
    const totalFormulas = championships.reduce((acc, ch) => {
        return acc + (Array.isArray(ch.formulas) ? ch.formulas.length : 0);
    }, 0);
    const withPhoto = championships.filter((ch) => !!ch.photo).length;
    const withoutFormulas = championships.filter((ch) => !(Array.isArray(ch.formulas) && ch.formulas.length > 0)).length;

    return (
        <div className="rs-page champ-page">
            <header className="rs-page-header">
                <div className="rs-page-header__text">
                    <span className="rs-kicker rs-kicker--red"><span className="rs-kicker__dot" />Gestione</span>
                    <h1 className="rs-page-header__title">Campionati</h1>
                    <p className="rs-page-header__subtitle">Competizioni e formule: ogni pilota e team viene iscritto a un campionato e a una sua formula.</p>
                </div>
                <div className="rs-page-header__actions">
                    <button type="button" className="rs-btn rs-btn--primary" onClick={() => setFormOpen(true)}>
                        <Icon.Plus /> Nuovo campionato
                    </button>
                </div>
            </header>

            {/* KPI */}
            <section className="rs-grid rs-grid--4 champ-stats" aria-label="Riepilogo">
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__value">{championships.length}</span>
                    <span className="rs-stat__label">Campionati</span>
                </div>
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__value">{totalFormulas}</span>
                    <span className="rs-stat__label">Formule</span>
                </div>
                <div className="rs-stat rs-stat--inline">
                    <span className="rs-stat__value">{withPhoto}</span>
                    <span className="rs-stat__label">Con immagine</span>
                </div>
                <div className={`rs-stat rs-stat--inline ${withoutFormulas > 0 ? 'rs-stat--red' : ''}`}>
                    <span className="rs-stat__value">{withoutFormulas}</span>
                    <span className="rs-stat__label">Senza formule</span>
                </div>
            </section>

            {/* FORM CREAZIONE (collassabile) */}
            <section className={`rs-card champ-form ${formOpen ? 'is-open' : ''}`}>
                <div
                    className="rs-card__head rs-card__head--toggle"
                    onClick={() => setFormOpen(o => !o)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={formOpen}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFormOpen(o => !o); } }}
                >
                    <span className="rs-card__title"><Icon.Plus /> Nuovo campionato</span>
                    <span className="champ-form__hint rs-hide-mobile">{formOpen ? 'Nome e immagine di copertina' : 'Apri per creare un campionato'}</span>
                    <Icon.ChevronDown className={`rs-chevron ${formOpen ? 'rs-chevron--open' : ''}`} />
                </div>

                {formOpen && (
                    <form className="rs-card__body champ-form__body" onSubmit={handleAddChampionship}>
                        <div className="rs-grid rs-grid--2 champ-form__grid">
                            <div className="rs-field">
                                <label className="rs-label">Nome del campionato</label>
                                <input
                                    type="text"
                                    className="rs-input"
                                    placeholder="Es. Formula GP Italia"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                                <span className="rs-help">Il nome compare in classifica, overlay OBS e nella selezione piloti.</span>
                            </div>
                            <div className="rs-field">
                                <label className="rs-label">Immagine <span className="rs-label__opt">opzionale</span></label>
                                <Dropzone
                                    file={photoFile}
                                    preview={photoPreview}
                                    onFileChange={handleFile}
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="rs-alert rs-alert--danger"><Icon.Alert /> {error}</div>
                        )}

                        <div className="champ-form__actions">
                            <button type="button" className="rs-btn rs-btn--ghost" onClick={() => setFormOpen(false)}>Chiudi</button>
                            <button className="rs-btn rs-btn--primary" type="submit" disabled={creating}>
                                {creating ? (
                                    <><span className="rs-spinner" /> Salvataggio…</>
                                ) : (
                                    <><Icon.Plus /> Crea campionato</>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </section>

            {/* ELENCO */}
            <section className="rs-section champ-list">
                <div className="rs-section__head">
                    <h2 className="rs-section__title">Campionati</h2>
                    <span className="rs-chip">Totale <strong>{championships.length}</strong></span>
                </div>

                {loading && (
                    <div className="champ-loading"><span className="rs-spinner" /> Caricamento campionati…</div>
                )}

                {!loading && championships.length === 0 && (
                    <div className="rs-empty">
                        <Icon.Trophy />
                        <strong>Nessun campionato</strong>
                        <p>Crea il primo campionato e aggiungi le sue formule: serviranno per iscrivere piloti e team.</p>
                        <button type="button" className="rs-btn rs-btn--sm rs-btn--primary" onClick={() => setFormOpen(true)}><Icon.Plus /> Nuovo campionato</button>
                    </div>
                )}

                {!loading && championships.length > 0 && (
                    <div className="rs-grid champ-grid">
                        {championships.map((ch) => {
                            const isBusy = !!busyById[ch.id];
                            const createdStr = ch.createdAt
                                ? new Date(ch.createdAt).toLocaleDateString('it-IT')
                                : "";
                            const formulas = Array.isArray(ch.formulas) ? ch.formulas : [];
                            const confirming = confirmId === ch.id;

                            return (
                                <article key={ch.id} className={`rs-card champ-card ${confirming ? 'rs-card--selected' : ''}`}>
                                    <div className="champ-card__media">
                                        {ch.photo ? (
                                            <img
                                                src={`${API_BASE}${ch.photo}`}
                                                alt={ch.name}
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="champ-card__nophoto">
                                                <IconImage size={26} />
                                                <span>Nessuna immagine</span>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="rs-btn rs-btn--sm rs-btn--icon champ-card__delete"
                                            title="Elimina campionato"
                                            aria-label="Elimina campionato"
                                            disabled={isBusy}
                                            onClick={() => setConfirmId(confirming ? null : ch.id)}
                                        >
                                            {isBusy ? <span className="rs-spinner" /> : <IconTrash />}
                                        </button>
                                    </div>

                                    <div className="champ-card__body">
                                        <div className="champ-card__head">
                                            <h3 className="champ-card__name" title={ch.name}>{ch.name}</h3>
                                            {createdStr && (
                                                <span className="rs-chip champ-card__date">
                                                    <IconCalendar />
                                                    {createdStr}
                                                </span>
                                            )}
                                        </div>

                                        {confirming && (
                                            <div className="rs-confirm champ-confirm">
                                                <div className="rs-confirm__text">
                                                    Eliminare definitivamente <strong>{ch.name}</strong> e le sue {formulas.length} formule?
                                                </div>
                                                <div className="rs-confirm__actions">
                                                    <button type="button" className="rs-btn rs-btn--sm rs-btn--ghost" onClick={() => setConfirmId(null)}>Annulla</button>
                                                    <button
                                                        type="button"
                                                        className="rs-btn rs-btn--sm rs-btn--danger"
                                                        disabled={isBusy}
                                                        onClick={() => { setConfirmId(null); handleDeleteChampionship(ch.id); }}
                                                    >
                                                        <IconTrash /> Elimina
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Formule */}
                                        <div className="champ-formulas">
                                            <div className="champ-formulas__head">
                                                <span className="rs-label"><Icon.Layers size={13} /> Formule</span>
                                                <span className={`rs-badge ${formulas.length === 0 ? 'rs-badge--warning' : ''}`}>{formulas.length}</span>
                                            </div>

                                            {formulas.length === 0 ? (
                                                <div className="champ-formulas__empty">Nessuna formula: aggiungine una per iscrivere piloti e team.</div>
                                            ) : (
                                                <div className="champ-formulas__list">
                                                    {formulas.map((f) => (
                                                        <span key={f.id} className="rs-badge champ-formula">
                                                            <span className="champ-formula__label">{f.label}</span>
                                                            <button
                                                                type="button"
                                                                className="champ-formula__remove"
                                                                title={`Rimuovi ${f.label}`}
                                                                aria-label={`Rimuovi ${f.label}`}
                                                                disabled={isBusy}
                                                                onClick={() => handleDeleteFormula(ch.id, f.id)}
                                                            >
                                                                <Icon.X size={11} />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="rs-input-group champ-formulas__add">
                                                <input
                                                    className="rs-input"
                                                    placeholder="Nuova formula (es. KZ2)"
                                                    value={formulaInputs[ch.id] || ""}
                                                    onChange={(e) => onChangeFormulaInput(ch.id, e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleAddFormula(ch.id);
                                                        }
                                                    }}
                                                    disabled={isBusy}
                                                    aria-label={`Nuova formula per ${ch.name}`}
                                                />
                                                <button
                                                    type="button"
                                                    className="rs-btn rs-btn--outline"
                                                    title="Aggiungi formula"
                                                    aria-label="Aggiungi formula"
                                                    disabled={isBusy || !(formulaInputs[ch.id] || "").trim()}
                                                    onClick={() => handleAddFormula(ch.id)}
                                                >
                                                    <Icon.Plus /> Aggiungi
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}
